import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Turnstile from "@/components/Turnstile";

const LOCS = ["West Bay Street office", "Lynden Pindling Intl Airport (NAS)", "Downtown Nassau", "Cable Beach", "Paradise Island", "Prince George Wharf (Cruise Port)", "Other / villa / hotel (custom)"];

const F = ({ label, children, testid }) => (
  <div>
    <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-1">{label}</p>
    {children}
  </div>
);

export default function GetQuote() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [tsToken, setTsToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    vehicle_id: "", start_date: "", end_date: "",
    pickup_location: LOCS[0], dropoff_location: "Same as pickup", pickup_time: "10:00",
    customer_name: "", customer_email: "", customer_phone: "", coupon_code: "", website: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles((r.data || []).filter((v) => v.active !== false))).catch(() => {});
  }, []);

  const submit = async () => {
    if (!f.vehicle_id || !f.start_date || !f.end_date || !f.customer_name.trim() || !f.customer_email.trim()) {
      return toast.error("Vehicle, dates, name and email are required");
    }
    if (!tsToken) return toast.error("Please complete the security check");
    setBusy(true);
    try {
      const { data } = await api.post("/quote/self", { ...f, turnstile_token: tsToken });
      if (data.quote_ref) toast.success(`Quote ${data.quote_ref} created — we also emailed you a copy`);
      navigate(`/quote/${data.token}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="self-quote-page" className="max-w-xl mx-auto px-6 pt-32 pb-24">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-ui text-muted-foreground hover:text-[#C71E2E] mb-6"><ArrowLeft size={14} /> Back to site</Link>
      <div className="rounded-3xl border border-stone-200 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.06)] overflow-hidden">
        <div className="h-2 bg-gradient-to-r from-[#C71E2E] to-[#8A1420]" />
        <div className="p-6 sm:p-8">
          <p className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#C71E2E] font-semibold">Instant Quote</p>
          <h1 className="mt-2 font-display text-3xl tracking-tight text-stone-900">Price your rental in seconds</h1>
          <p className="mt-2 text-sm font-ui text-muted-foreground">Pick your vehicle and dates — we’ll email you a detailed quote with an accept &amp; pay link, valid for 7 days.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            <div className="sm:col-span-2">
              <F label="Vehicle">
                <select data-testid="self-quote-vehicle" value={f.vehicle_id} onChange={set("vehicle_id")} className="input-dark w-full h-10 rounded-xl px-3 text-sm font-ui">
                  <option value="">Select vehicle…</option>
                  {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} — ${v.price_per_day}/day</option>)}
                </select>
              </F>
            </div>
            <F label="Pickup date"><Input data-testid="self-quote-start" type="date" value={f.start_date} onChange={set("start_date")} className="input-dark" /></F>
            <F label="Return date"><Input data-testid="self-quote-end" type="date" value={f.end_date} onChange={set("end_date")} className="input-dark" /></F>
            <F label="Pick-up location">
              <select data-testid="self-quote-pickup" value={f.pickup_location} onChange={set("pickup_location")} className="input-dark w-full h-10 rounded-xl px-3 text-sm font-ui">{LOCS.map((l) => <option key={l}>{l}</option>)}</select>
            </F>
            <F label="Drop-off location">
              <select data-testid="self-quote-dropoff" value={f.dropoff_location} onChange={set("dropoff_location")} className="input-dark w-full h-10 rounded-xl px-3 text-sm font-ui">{["Same as pickup", ...LOCS].map((l) => <option key={l}>{l}</option>)}</select>
            </F>
            <F label="Your name"><Input data-testid="self-quote-name" value={f.customer_name} onChange={set("customer_name")} className="input-dark" /></F>
            <F label="Email"><Input data-testid="self-quote-email" type="email" value={f.customer_email} onChange={set("customer_email")} className="input-dark" /></F>
            <F label="Phone / WhatsApp (optional)"><Input data-testid="self-quote-phone" value={f.customer_phone} onChange={set("customer_phone")} className="input-dark" /></F>
            <F label="Discount code (optional)"><Input data-testid="self-quote-coupon" value={f.coupon_code} onChange={set("coupon_code")} className="input-dark uppercase" /></F>
          </div>

          <input type="text" name="website" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

          <div className="mt-5">
            <Turnstile action="self-quote" onToken={setTsToken} />
          </div>

          <Button data-testid="self-quote-submit" onClick={submit} disabled={busy} className="mt-5 w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui h-12 text-base">
            {busy ? "Preparing your quote…" : <>Get My Quote <ArrowRight size={16} className="ml-2" /></>}
          </Button>
          <p className="mt-4 text-xs font-ui text-muted-foreground text-center">No payment taken here — review the quote first, then decide.</p>
        </div>
      </div>
    </div>
  );
}
